import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { useFormik } from "formik";
import { CertificacionI } from "interfaces/Certificacion";
import { CombinatoriaI } from "interfaces/Combinatoria";
import { DiseñoCurricularI } from "interfaces/DiseñoCurricular";
import { EspecialidadI } from "interfaces/especialidad";
import { NormativaI } from "interfaces/Normativa";
import { OrientacionI } from "interfaces/Orientacion";
import { TituloI } from "interfaces/Titulo";
import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { createPlanesEstudioSchema } from "schemas/planesEstudio/createPlanesEstudio";
import { CertificacionesABM } from "screens/Certificaciones/CertificacionesABM/CertificacionesABM";
import { DiseñosCurricularesABM } from "screens/DiseñosCurriculares/DiseñosCurricularesABM/DiseñosCurricularesABM";
import { EspecialidadABM } from "screens/Especialidades/EspecialidadABM/EspecialidadABM";
import { NormativasABM } from "screens/Normativas/NormativasABM/NormativasABM";
import { OrientacionesABM } from "screens/Orientaciones/OrientacionesABM/OrientacionesABM";
import { TitulosABM } from "screens/Titulos/TitulosABM/TitulosABM";
import { getCertificaciones } from "services/certificacion";
import { getCombinatorias } from "services/combinatoria";
import { getDiseñosCurriculares } from "services/diseñoCurricular";
import { getEspecialidades } from "services/especialidad";
import { getNormativas } from "services/normativas";
import { getOrientaciones } from "services/orientacion";
import {
  getPlanEstudioById,
  postPlanEstudio,
  putPlanEstudio,
} from "services/planesEstudio";
import { getTitulos } from "services/titulos";
import { fetchAlcances } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { formatDate } from "utils/formatUtils";

export type abmActionI = "create" | "edit";

interface Props {
  abmAction?: abmActionI;
}

interface planesABMValuesI {
  id?: string | number;
  nombre: string;
  descripcion: string;
  cantidad_anios: number | string;
  alcance_id: number | string;
  disenio_curricular_id: number | string;
  arr_titulos_id: (number | string)[];
  arr_certificaciones_id: (number | string)[];
  arr_orientaciones_id: (number | string)[];
  arr_normativas_id: (number | string)[];
  arr_especialidades_id: (number | string)[];
  arr_combinatorias_id: (number | string)[];
  fecha_publicacion: Date | string;
  aplicaValidezNacional: boolean;
}

interface defaultOptionsSelected {
  default_selected_disenio_curricular: DiseñoCurricularI | null;
  default_selected_notificaciones: NormativaI[] | null;
  default_selected_titulos: TituloI[] | null;
  default_selected_certificaciones: CertificacionI[] | null;
  default_selected_orientaciones: OrientacionI[] | null;
  default_selected_especialidades: EspecialidadI[] | null;
  default_selected_combinatorias: CombinatoriaI[] | null;
}

export const PlanesABM = ({ abmAction = "create" }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { alcances, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );
  const [modalsActionType, setModalsActionType] = useState<
    "create" | "edit" | undefined
  >();
  const [showModalABM, setShowModalABM] = useState({
    diseñosCurriculares: false,
    titulos: false,
    normativas: false,
    certificaciones: false,
    orientaciones: false,
    especialidades: false,
    aplicaValidezNacional: false,
  });

  const [isLoading, setIsLoading] = useState({
    form: false,
  });

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_selected_disenio_curricular: null,
      default_selected_notificaciones: null,
      default_selected_titulos: null,
      default_selected_certificaciones: null,
      default_selected_orientaciones: null,
      default_selected_especialidades: null,
      default_selected_combinatorias: null,
    });

  const defaultInitialValues: planesABMValuesI = {
    nombre: "",
    descripcion: "",
    cantidad_anios: "",
    alcance_id: "",
    disenio_curricular_id: "",
    arr_titulos_id: [],
    arr_certificaciones_id: [],
    arr_orientaciones_id: [],
    arr_normativas_id: [],
    arr_especialidades_id: [],
    arr_combinatorias_id: [],
    fecha_publicacion: "",
    aplicaValidezNacional: false,
  };

  const handleReturn = () => {
    navigate(PRIVATE_ROUTE.PLANES);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      if (params.fecha_publicacion) {
        params.fecha_publicacion = new Date(
          `${params.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }
      const newPlan = await postPlanEstudio({ params });
      handleReturn();
      showSuccessToast("¡El plan de estudio fue creado con éxito!");
    } catch (err: any) {
      showErrorToast("El plan de estudio no pudo ser creado.");
      console.log(err.message);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      if (params.fecha_publicacion) {
        params.fecha_publicacion = new Date(
          `${params.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }
      const newPlan = await putPlanEstudio({ params });
      handleReturn();
      showSuccessToast("¡El plan de estudio fue editado con éxito!");
    } catch (err: any) {
      showErrorToast("El plan de estudio no pudo ser editado.");
      console.log(err.message);
    }
  };

  const addNewElemento = ({
    key,
    elemento,
    isMulti,
  }: {
    key: keyof defaultOptionsSelected;
    elemento: any;
    isMulti?: boolean;
  }) => {
    setDefaultOptionsSelected((st: defaultOptionsSelected) => {
      const getPrevMultiSt = () =>
        st[key] && Array.isArray(st[key]) ? (st[key] as any) : [];
      return {
        ...st,
        [key]: isMulti ? [...getPrevMultiSt(), elemento] : elemento,
      };
    });
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues: defaultInitialValues,
    validationSchema: createPlanesEstudioSchema,
    onSubmit: abmAction == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const handleCreateModal = (modalName: string) => {
    setModalsActionType("create");
    setShowModalABM((st) => ({ ...st, [modalName]: true }));
  };

  const handleCloseModal = (modalName: string) => {
    setShowModalABM((st) => ({ ...st, [modalName]: false }));
    setTimeout(() => setModalsActionType(undefined), 500);
  };

  const loadEditForm = async () => {
    const errorLoadingNotificacionResolve = () => {
      showErrorToast("No fue posible cargar el plan de estudio a editar");
      navigate(PRIVATE_ROUTE.PLANES);
    };

    try {
      setIsLoading((st) => ({ ...st, form: true }));

      if (!id) return errorLoadingNotificacionResolve();

      const planEstudio = await getPlanEstudioById(id);

      if (!planEstudio) return errorLoadingNotificacionResolve();

      console.log({ planEstudio });

      const initialValues = {
        id: planEstudio.id,
        nombre: planEstudio.nombre,
        descripcion: planEstudio.descripcion,
        cantidad_anios: planEstudio.cantidad_anios,
        fecha_publicacion: formatDate.formDate(planEstudio.fecha_publicacion),
        alcance_id: planEstudio.alcance.id,
        disenio_curricular_id: planEstudio.disenio_curricular.id,
        arr_titulos_id: planEstudio.titulos.map((ctT) => ctT.id),
        arr_normativas_id: planEstudio.normativas.map((ctR) => ctR.id),
        arr_certificaciones_id: planEstudio.certificaciones.map(
          (ctR) => ctR.id,
        ),
        arr_orientaciones_id: planEstudio.orientaciones.map((ctR) => ctR.id),
        arr_especialidades_id: planEstudio.especialidades.map((ctR) => ctR.id),
        arr_combinatorias_id: planEstudio.combinatorias.map((ctR) => ctR.id),
        aplicaValidezNacional: planEstudio?.aplicaValidezNacional ?? false,
      };

      setDefaultOptionsSelected({
        default_selected_disenio_curricular: planEstudio.disenio_curricular,
        default_selected_notificaciones: planEstudio.normativas,
        default_selected_titulos: planEstudio.titulos,
        default_selected_orientaciones: planEstudio.orientaciones,
        default_selected_certificaciones: planEstudio.certificaciones,
        default_selected_especialidades: planEstudio.especialidades,
        default_selected_combinatorias: planEstudio.combinatorias,
      });

      setValues(initialValues);
    } catch (err) {
      errorLoadingNotificacionResolve();
    } finally {
      setIsLoading((st) => ({ ...st, form: false }));
    }
  };

  const addNewDiseño = (elemento: DiseñoCurricularI) => {
    addNewElemento({
      key: "default_selected_disenio_curricular",
      elemento,
      isMulti: false,
    });
    setValues({ ...values, disenio_curricular_id: elemento.id });
  };

  const addNewNormativa = (elemento: NormativaI) => {
    addNewElemento({
      key: "default_selected_notificaciones",
      elemento,
      isMulti: true,
    });
    setValues({
      ...values,
      arr_normativas_id: [...values.arr_normativas_id, elemento.id],
    });
  };

  const addNewTitulo = (elemento: TituloI) => {
    addNewElemento({
      key: "default_selected_titulos",
      elemento,
      isMulti: true,
    });
    setValues({
      ...values,
      arr_titulos_id: [...values.arr_titulos_id, elemento.id],
    });
  };

  const addNewCertificacion = (elemento: CertificacionI) => {
    addNewElemento({
      key: "default_selected_certificaciones",
      elemento,
      isMulti: true,
    });
    setValues({
      ...values,
      arr_certificaciones_id: [...values.arr_certificaciones_id, elemento.id],
    });
  };

  const addNewOrientacion = (elemento: OrientacionI) => {
    addNewElemento({
      key: "default_selected_orientaciones",
      elemento,
      isMulti: true,
    });
    setValues({
      ...values,
      arr_orientaciones_id: [...values.arr_orientaciones_id, elemento.id],
    });
  };

  const addNewEspecialidad = (elemento: EspecialidadI) => {
    addNewElemento({
      key: "default_selected_especialidades",
      elemento,
      isMulti: true,
    });
    setValues({
      ...values,
      arr_especialidades_id: [...values.arr_especialidades_id, elemento.id],
    });
  };

  useEffect(() => {
    if (abmAction == "edit") loadEditForm();
    if (!alcances) dispatch(fetchAlcances());
  }, []);

  return (
    <section>
      <h1 className="section-title">
        {abmAction == "edit"
          ? "Editar plan de estudio"
          : "Nuevo plan de estudio"}
      </h1>

      <h2 className="section-subtitle mt-2">
        Edita o crea un nuevo plan de estudio.
      </h2>
      <Card>
        <form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col lg={4} className="mb-3">
              <Input
                id="nombre"
                onChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                variant="gray-container"
                title="Nombre"
                size="lg"
                required
              />
            </Col>

            <Col lg={4} className="mb-3">
              <Input
                id="cantidad_anios"
                type="number"
                onChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                variant="gray-container"
                title="Cantidad de años"
                size="lg"
                min={1}
                max={99}
                required
              />
            </Col>

            <Col sm={4} className="mb-3">
              <Select
                id="alcance_id"
                title="Alcance"
                size="lg"
                optionsFormatConfig={{ value: "id", label: "nombre" }}
                options={alcances ?? []}
                isLoading={loadingAppData.alcances}
                // disabled={isLoading.gradosNivel}
                placeholder="Seleccionar"
                variant="gray-container"
                required
                values={values}
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Col>
            <Col sm={4} className="mb-3">
              <Input
                id="fecha_publicacion"
                type="date"
                onChange={handleChange}
                values={values}
                variant="gray-container"
                errors={errors}
                onBlur={handleBlur}
                title="Fecha de publicación"
                touched={touched}
                required
                size="lg"
                // disabled={!values.isProgrammed}
                // minDate={format(new Date(), 'yyyy-MM-dd')}
              />
            </Col>
            <Col sm={4} style={{ display: "flex", alignItems: "center" }}>
              <label
                className="form-check-label"
                htmlFor="aplicaValidezNacional"
                style={{ marginRight: "10px" }}
              >
                ¿Aplica Validez Nacional?
              </label>
              <input
                id="aplicaValidezNacional"
                name="aplicaValidezNacional"
                type="checkbox"
                className="form-check-input"
                checked={values.aplicaValidezNacional}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{ margin: "0px" }}
              />
              {errors.aplicaValidezNacional &&
                touched.aplicaValidezNacional && (
                  <div className="text-danger">
                    {errors.aplicaValidezNacional}
                  </div>
                )}
            </Col>
          </Row>
          <Row>
            <Col sm={8} className="mb-3">
              <Input
                id="descripcion"
                onChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                variant="gray-container"
                title="Descripcion"
                size="lg"
                type="textarea"
                maxLength={260}
                defaultHeight={"100px"}
                              />
            </Col>
          </Row>
          <hr></hr>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="disenio_curricular_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de diseño curricular"}
                size="lg"
                title="Diseño curricular"
                getter={() => getDiseñosCurriculares({ limit: 50 })}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_disenio_curricular"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("diseñosCurriculares")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nuevo diseño
              </Button>
            </Col>
          </Row>
          <hr></hr>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_normativas_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de la normativa"}
                size="lg"
                title="Normativas"
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getNormativas({ [key]: value, limit: 50 });
                }}
                paramKey={"nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_notificaciones"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("normativas")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva normativa
              </Button>
            </Col>
          </Row>
          <hr></hr>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_titulos_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de título"}
                size="lg"
                title="Títulos"
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getTitulos({ [key]: value, limit: 50 });
                }}
                paramKey={"nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_titulos"]
                }
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("titulos")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nuevo titulo
              </Button>
            </Col>
          </Row>
          <hr></hr>

          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_certificaciones_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de certificación"}
                size="lg"
                title="Certificaciones"
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getCertificaciones({ [key]: value, limit: 50 });
                }}
                paramKey={"nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_certificaciones"]
                }
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("certificaciones")}
                variant="secondary"
                size={"lg"}
                style={{
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva certificación
              </Button>
            </Col>
          </Row>

          <hr></hr>

          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_especialidades_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de especialidad"}
                size="lg"
                title="Especialidades"
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getEspecialidades({ [key]: value, limit: 50 });
                }}
                paramKey={"nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_especialidades"]
                }
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("especialidades")}
                variant="secondary"
                size={"lg"}
                style={{
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva especialidad
              </Button>
            </Col>
          </Row>

          <hr></hr>

          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_orientaciones_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de orientación"}
                size="lg"
                title="Orientaciones"
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getOrientaciones({ [key]: value, limit: 50 });
                }}
                paramKey={"nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NormativaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_orientaciones"]
                }
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("orientaciones")}
                variant="secondary"
                size={"lg"}
                style={{
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva orientación
              </Button>
            </Col>
          </Row>
          <hr></hr>

          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="arr_combinatorias_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar combinatoria"}
                size="lg"
                title="Nivel - Modalidad - Opción pedagógica "
                getter={(params) => {
                  const [key, value] = Object.entries(params)[0];
                  return getCombinatorias({ [key]: value, limit: 50 });
                }}
                paramKey={"combinatoria_nombre"}
                isMulti={true}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: CombinatoriaI) =>
                    `${el.nivel.nombre} - ${el.modalidad.nombre} - ${el.opcion_pedagogica.nombre} `,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_combinatorias"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
          </Row>

          <hr></hr>

          <Row>
            <Col>
              <div className="w-100 d-flex column-gap-3 justify-content-end">
                <Button variant="secondary" size={"lg"} onClick={handleReturn}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Confirmando..." : "Confirmar"}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </Card>

      <DiseñosCurricularesABM
        showModal={showModalABM.diseñosCurriculares}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("diseñosCurriculares")}
        onSuccess={(newEl: DiseñoCurricularI) => addNewDiseño(newEl)}
      />
      <NormativasABM
        showModal={showModalABM.normativas}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("normativas")}
        onSuccess={(newEl: NormativaI) => addNewNormativa(newEl)}
      />
      <TitulosABM
        showModal={showModalABM.titulos}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("titulos")}
        onSuccess={(newEl: TituloI) => addNewTitulo(newEl)}
      />

      <CertificacionesABM
        showModal={showModalABM.certificaciones}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("certificaciones")}
        onSuccess={(newEl: CertificacionI) => addNewCertificacion(newEl)}
      />

      <OrientacionesABM
        showModal={showModalABM.orientaciones}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("orientaciones")}
        onSuccess={(newEl: OrientacionI) => addNewOrientacion(newEl)}
      />

      <EspecialidadABM
        showModal={showModalABM.especialidades}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("especialidades")}
        onSuccess={(newEl: EspecialidadI) => addNewEspecialidad(newEl)}
      />
    </section>
  );
};
